import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import leadsApi from "../api/leadsApi";
import campaignApi from "../api/campaignApi";
import "./SentEmails.css";
import { useAuthStore } from "../store/useAuthStore";

const EMAIL_TYPE_DETAILS = {
  intro: {
    title: "Intro Emails",
    singularTitle: "Intro Mail",
    icon: "📩",
    description: "All successfully sent introduction emails",
  },

  portfolio: {
    title: "Portfolio Emails",
    singularTitle: "Portfolio Mail",
    icon: "📁",
    description: "All successfully sent portfolio emails",
  },

  proposal: {
    title: "Proposal Emails",
    singularTitle: "Proposal Mail",
    icon: "📄",
    description: "All successfully sent proposal emails",
  },
};

// --- period filter (Month / Quarter / Year) -----------------------------
// Same pattern used on the CRM Dashboard and Leads pages, kept consistent
// here so the filter means the same thing across the whole CRM module.

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const QUARTERS = [
  { key: "q1", months: [0, 1, 2] }, // Jan-Mar
  { key: "q2", months: [3, 4, 5] }, // Apr-Jun
  { key: "q3", months: [6, 7, 8] }, // Jul-Sep
  { key: "q4", months: [9, 10, 11] }, // Oct-Dec
];

const getQuarterLabel = (quarter, year) => {
  const startMonth = MONTH_NAMES[quarter.months[0]];
  const endMonth = MONTH_NAMES[quarter.months[2]];
  const shortYear = String(year).slice(-2);
  return `${startMonth}-${endMonth}-${shortYear}`;
};

// Year dropdown list: current year + every year found in the sent emails
// data. Keeps the current year always visible (even with no data yet) and
// automatically grows as new years' emails come in.
const getAvailableYears = (emails) => {
  const years = new Set([new Date().getFullYear()]);

  emails.forEach((email) => {
    if (!email?.sentAt) return;
    const date = new Date(email.sentAt);
    if (!Number.isNaN(date.getTime())) {
      years.add(date.getFullYear());
    }
  });

  return [...years].sort((a, b) => a - b);
};

// Current week = Monday through Sunday, same convention used elsewhere in
// the CRM (meetings-this-week, etc).
const getCurrentWeekRange = () => {
  const now = new Date();

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diffToMonday);

  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

const isWithinFilter = (dateValue, { year, month, quarter, weekly }) => {
  if (!dateValue) return false;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  // Every date filter works together.
  // Example: This Week + Year + Month + Quarter can all be applied at once.
  if (weekly) {
    const { start, end } = getCurrentWeekRange();
    if (date < start || date > end) return false;
  }

  if (year !== "all" && date.getFullYear() !== Number(year)) {
    return false;
  }

  if (month !== "all" && date.getMonth() !== Number(month)) {
    return false;
  }

  if (quarter !== "all") {
    const selectedQuarter = QUARTERS.find((item) => item.key === quarter);

    if (selectedQuarter && !selectedQuarter.months.includes(date.getMonth())) {
      return false;
    }
  }

  return true;
};

const normalizeEmailType = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    normalizedValue === "intro" ||
    normalizedValue === "introduction" ||
    normalizedValue === "intro_mail" ||
    normalizedValue === "intro_email"
  ) {
    return "intro";
  }

  if (
    normalizedValue === "portfolio" ||
    normalizedValue === "portfolio_mail" ||
    normalizedValue === "portfolio_email"
  ) {
    return "portfolio";
  }

  if (
    normalizedValue === "proposal" ||
    normalizedValue === "proposal_mail" ||
    normalizedValue === "proposal_email"
  ) {
    return "proposal";
  }

  return normalizedValue;
};

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }

        return String(item?.email || item?.address || item?.value || "").trim();
      })
      .filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeAttachments = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((attachment, index) => {
    if (typeof attachment === "string") {
      return {
        _id: `attachment-${index}`,
        name: attachment.split("/").pop() || "Attachment",
        url: attachment,
      };
    }

    return {
      ...attachment,

      _id: attachment?._id || attachment?.id || `attachment-${index}`,

      name:
        attachment?.originalName ||
        attachment?.originalname ||
        attachment?.filename ||
        attachment?.name ||
        "Attachment",

      url: attachment?.url || attachment?.location || attachment?.path || "",
    };
  });
};

const normalizeSentEmail = (record, index) => {
  return {
    id: record?._id || record?.id || `sent-email-${index}`,

    leadId:
      record?.leadId?._id ||
      record?.leadId ||
      record?.lead?._id ||
      record?.lead ||
      "",

    emailType: normalizeEmailType(
      record?.emailType || record?.actionType || record?.mailType,
    ),

    company:
      record?.companyName ||
      record?.company ||
      record?.leadCompany ||
      record?.leadId?.companyName ||
      record?.leadId?.company ||
      "—",

    contact:
      record?.contactName ||
      record?.contact ||
      record?.leadContact ||
      record?.leadId?.contactName ||
      record?.leadId?.name ||
      "—",

    leadEmail:
      record?.leadEmail || record?.contactEmail || record?.leadId?.email || "",

    subject: record?.subject || record?.emailSubject || "No subject",

    message: record?.message || record?.body || record?.emailBody || "",

    recipients: normalizeStringArray(
      record?.recipients || record?.to || record?.recipientEmails,
    ),

    cc: normalizeStringArray(record?.cc || record?.ccRecipients),

    bcc: normalizeStringArray(record?.bcc || record?.bccRecipients),

    attachments: normalizeAttachments(record?.attachments),

    sentBy:
      record?.sentBy?.name ||
      record?.sentBy?.fullName ||
      record?.createdBy?.name ||
      record?.createdBy?.fullName ||
      record?.createdByName ||
      record?.senderName ||
      "Admin",

    sentAt: record?.sentAt || record?.createdAt || record?.date || null,

    status: record?.status || "sent",

    messageId: record?.messageId || "",

    proposalReference: record?.proposalReference || record?.proposalNo || "",

    proposalValue: record?.proposalValue || record?.amount || "",

    campaign:
      record?.campaign ||
      record?.campaignName ||
      record?.leadCampaign ||
      record?.leadId?.campaign ||
      "",
  };
};

const normalizeScheduledEmail = (record, index) => {
  const lead =
    record?.lead && typeof record.lead === "object" ? record.lead : {};
  const createdBy =
    record?.createdBy && typeof record.createdBy === "object"
      ? record.createdBy
      : {};

  return {
    id: record?._id || record?.id || `scheduled-email-${index}`,
    scheduledEmailId: record?._id || record?.id || "",
    leadId: lead?._id || record?.lead || "",
    emailType: normalizeEmailType(record?.actionType),
    company: lead?.company || "—",
    contact:
      lead?.contact ||
      `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim() ||
      "—",
    leadEmail: lead?.email || "",
    subject: record?.subject || "No subject",
    message: record?.message || "",
    recipients: normalizeStringArray(record?.to),
    cc: normalizeStringArray(record?.cc),
    bcc: [],
    attachments: normalizeAttachments(record?.attachments),
    sentBy:
      createdBy?.name ||
      createdBy?.fullName ||
      createdBy?.email ||
      record?.createdByName ||
      "Admin",
    sentAt: null,
    scheduledFor: record?.scheduledFor || null,
    timezone: record?.timezone || "Asia/Kolkata",
    status: record?.status || "scheduled",
    messageId: "",
    proposalReference: record?.proposalReference || "",
    proposalValue: record?.proposalValue ?? "",
    campaign: lead?.campaign || "",
    isScheduled: true,
  };
};

// Inline styles for the Year/Month/Quarter filter row. These are applied
// directly (not just via className) so the dropdowns always lay out
// side-by-side, regardless of whatever ".sent-emails-period-filter" /
// ".sent-emails-period-btn" rules already exist in SentEmails.css (those
// were written for the old button-toggle layout and were stacking the new
// <select> elements on top of each other).
const periodFilterInlineStyles = {
  wrap: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    position: "static",
  },
  select: {
    position: "static",
    display: "inline-block",
    width: "auto",
    minWidth: 110,
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#334155",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  weeklyButton: {
    position: "static",
    display: "inline-block",
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#334155",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  weeklyButtonActive: {
    background: "#111a3d",
    borderColor: "#111a3d",
    color: "#fff",
  },
};

const SentEmails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { authUser } = useAuthStore();

  const currentRole = String(authUser?.role || "")
    .trim()
    .toLowerCase();

  const isAdmin = currentRole === "admin";
  const [sentEmails, setSentEmails] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [filterQuarter, setFilterQuarter] = useState("all");
  const [filterWeekly, setFilterWeekly] = useState(false);
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [filterDeliveryStatus, setFilterDeliveryStatus] = useState("all");

  const [campaigns, setCampaigns] = useState([]);
  const [leadCampaignMap, setLeadCampaignMap] = useState({});

  const [selectedEmail, setSelectedEmail] = useState(null);

  // URL se selected tab decide hoga.
  const activeEmailType = useMemo(() => {
    if (location.pathname.includes("/sent-emails/portfolio")) {
      return "portfolio";
    }

    if (location.pathname.includes("/sent-emails/proposal")) {
      return "proposal";
    }

    return "intro";
  }, [location.pathname]);

  const currentTypeDetails = EMAIL_TYPE_DETAILS[activeEmailType];

  /*
    Backend endpoint:
    GET /api/leads/sent-emails
  */
  const fetchSentEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [
        emailResponse,
        scheduledResponse,
        leadsResponse,
        campaignResponse,
      ] = await Promise.all([
        leadsApi.getSentEmails(),
        typeof leadsApi.getAllScheduledEmails === "function"
          ? leadsApi.getAllScheduledEmails()
          : Promise.resolve({ scheduledEmails: [] }),
        leadsApi.getAll(),
        campaignApi.getAll(),
      ]);

      const records =
        emailResponse?.sentEmails ||
        emailResponse?.emails ||
        emailResponse?.data?.sentEmails ||
        emailResponse?.data?.emails ||
        emailResponse?.data ||
        emailResponse ||
        [];

      const scheduledRecords =
        scheduledResponse?.scheduledEmails ||
        scheduledResponse?.data?.scheduledEmails ||
        scheduledResponse?.data ||
        [];

      const leadRecords =
        leadsResponse?.leads ||
        leadsResponse?.data?.leads ||
        leadsResponse?.data ||
        leadsResponse ||
        [];

      const campaignRecords =
        campaignResponse?.campaigns ||
        campaignResponse?.data?.campaigns ||
        campaignResponse?.data ||
        campaignResponse ||
        [];

      const campaignMap = {};

      if (Array.isArray(leadRecords)) {
        leadRecords.forEach((lead) => {
          if (lead?._id) {
            campaignMap[String(lead._id)] = String(lead?.campaign || "").trim();
          }
        });
      }

      setLeadCampaignMap(campaignMap);
      setCampaigns(Array.isArray(campaignRecords) ? campaignRecords : []);

      const normalizedRecords = Array.isArray(records)
        ? records.map((record, index) => {
            const normalized = normalizeSentEmail(record, index);

            return {
              ...normalized,
              campaign:
                normalized.campaign ||
                campaignMap[String(normalized.leadId || "")] ||
                "",
            };
          })
        : [];

      setSentEmails(normalizedRecords);

      const normalizedScheduledRecords = Array.isArray(scheduledRecords)
        ? scheduledRecords.map((record, index) => {
            const normalized = normalizeScheduledEmail(record, index);
            return {
              ...normalized,
              campaign:
                normalized.campaign ||
                campaignMap[String(normalized.leadId || "")] ||
                "",
            };
          })
        : [];

      setScheduledEmails(normalizedScheduledRecords);
    } catch (fetchError) {
      console.error("Failed to fetch sent emails:", fetchError);

      setError(
        fetchError?.response?.data?.message ||
          fetchError?.message ||
          "Unable to load sent email records.",
      );

      setSentEmails([]);
      setScheduledEmails([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSentEmails();
  }, [fetchSentEmails]);

  const allEmailRecords = useMemo(
    () => [...sentEmails, ...scheduledEmails],
    [sentEmails, scheduledEmails],
  );

  const availableYears = useMemo(() => {
    const years = new Set(getAvailableYears(sentEmails));
    scheduledEmails.forEach((email) => {
      if (!email?.scheduledFor) return;
      const date = new Date(email.scheduledFor);
      if (!Number.isNaN(date.getTime())) years.add(date.getFullYear());
    });
    return [...years].sort((a, b) => a - b);
  }, [sentEmails, scheduledEmails]);

  const availableCampaigns = useMemo(() => {
    const masterNames = campaigns
      .filter((item) => item?.isActive !== false)
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean);

    const emailCampaigns = allEmailRecords
      .map((email) => String(email?.campaign || "").trim())
      .filter(Boolean);

    const mappedLeadCampaigns = Object.values(leadCampaignMap)
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    return [
      ...new Set([...masterNames, ...emailCampaigns, ...mappedLeadCampaigns]),
    ].sort((a, b) => a.localeCompare(b));
  }, [campaigns, allEmailRecords, leadCampaignMap]);

  const matchesDateFilter = useCallback(
    (sentAt) =>
      isWithinFilter(sentAt, {
        year: filterYear,
        month: filterMonth,
        quarter: filterQuarter,
        weekly: filterWeekly,
      }),
    [filterYear, filterMonth, filterQuarter, filterWeekly],
  );

  const filteredEmails = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return allEmailRecords
      .filter((email) => email.emailType === activeEmailType)
      .filter((email) =>
        matchesDateFilter(
          email.isScheduled ? email.scheduledFor : email.sentAt,
        ),
      )
      .filter((email) => {
        if (filterDeliveryStatus === "scheduled") {
          return email.isScheduled === true;
        }

        if (filterDeliveryStatus === "sent") {
          return email.isScheduled !== true;
        }

        return true;
      })
      .filter((email) => {
        if (filterCampaign === "all") {
          return true;
        }

        return String(email?.campaign || "").trim() === filterCampaign;
      })
      .filter((email) => {
        if (!searchValue) {
          return true;
        }

        const searchableText = [
          email.company,
          email.contact,
          email.leadEmail,
          email.subject,
          email.message,
          email.sentBy,
          email.proposalReference,
          email.campaign,
          ...(email.recipients || []),
          ...(email.cc || []),
        ]
          .join(" ")
          .toLowerCase();

        return searchableText.includes(searchValue);
      })
      .sort((firstEmail, secondEmail) => {
        const firstDate = firstEmail.isScheduled
          ? firstEmail.scheduledFor
          : firstEmail.sentAt;
        const secondDate = secondEmail.isScheduled
          ? secondEmail.scheduledFor
          : secondEmail.sentAt;

        const firstTime = firstDate ? new Date(firstDate).getTime() : 0;
        const secondTime = secondDate ? new Date(secondDate).getTime() : 0;

        return secondTime - firstTime;
      });
  }, [
    allEmailRecords,
    activeEmailType,
    search,
    matchesDateFilter,
    filterCampaign,
    filterDeliveryStatus,
  ]);

  const emailCounts = useMemo(() => {
    return {
      intro: sentEmails.filter((email) => email.emailType === "intro").length,

      portfolio: sentEmails.filter((email) => email.emailType === "portfolio")
        .length,

      proposal: sentEmails.filter((email) => email.emailType === "proposal")
        .length,
    };
  }, [sentEmails]);

  const scheduledCounts = useMemo(
    () => ({
      intro: scheduledEmails.filter((email) => email.emailType === "intro")
        .length,
      portfolio: scheduledEmails.filter(
        (email) => email.emailType === "portfolio",
      ).length,
      proposal: scheduledEmails.filter(
        (email) => email.emailType === "proposal",
      ).length,
      total: scheduledEmails.length,
    }),
    [scheduledEmails],
  );

  const formatDate = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (value) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatProposalValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    if (Number.isNaN(Number(value))) {
      return String(value);
    }

    return Number(value).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const handleOpenLead = (leadId) => {
    if (!leadId) {
      return;
    }

    navigate(`/leads/${leadId}`);
  };

  return (
    <div className="sent-emails-page">
      {/* Header */}
      <div className="sent-emails-header">
        <div>
          <h1>
            {currentTypeDetails.icon} {currentTypeDetails.title}
          </h1>

          <p>{currentTypeDetails.description}</p>
        </div>

        <button
          type="button"
          className="sent-emails-refresh-btn"
          onClick={fetchSentEmails}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "↻ Refresh"}
        </button>
      </div>

      {/* Email type tabs */}
      <div className="sent-emails-tabs">
        <button
          type="button"
          className={
            activeEmailType === "intro"
              ? "sent-email-tab active"
              : "sent-email-tab"
          }
          onClick={() => navigate("/sent-emails/intro")}
        >
          <span>📩 Intro Mail</span>
          <strong>{emailCounts.intro}</strong>
        </button>

        <button
          type="button"
          className={
            activeEmailType === "portfolio"
              ? "sent-email-tab active"
              : "sent-email-tab"
          }
          onClick={() => navigate("/sent-emails/portfolio")}
        >
          <span>📁 Portfolio Mail</span>
          <strong>{emailCounts.portfolio}</strong>
        </button>

        <button
          type="button"
          className={
            activeEmailType === "proposal"
              ? "sent-email-tab active"
              : "sent-email-tab"
          }
          onClick={() => navigate("/sent-emails/proposal")}
        >
          <span>📄 Proposal Mail</span>
          <strong>{emailCounts.proposal}</strong>
        </button>
      </div>

      {/* Summary cards */}
      <div className="sent-emails-summary-grid">
        <div className="sent-email-summary-card">
          <span>Current List</span>
          <strong>{filteredEmails.length}</strong>
          <small>
            Sent + scheduled {currentTypeDetails.title.toLowerCase()}
          </small>
        </div>

        <div className="sent-email-summary-card">
          <span>Intro Sent</span>
          <strong>{emailCounts.intro}</strong>
          <small>Total intro emails</small>
        </div>

        <div className="sent-email-summary-card">
          <span>Portfolio Sent</span>
          <strong>{emailCounts.portfolio}</strong>
          <small>Total portfolio emails</small>
        </div>

        <div className="sent-email-summary-card">
          <span>Proposal Sent</span>
          <strong>{emailCounts.proposal}</strong>
          <small>Total proposal emails</small>
        </div>

        <div className="sent-email-summary-card">
          <span>Scheduled</span>
          <strong>{scheduledCounts[activeEmailType] || 0}</strong>
          <small>
            Pending {currentTypeDetails.singularTitle.toLowerCase()}
          </small>
        </div>
      </div>

      {/* Search and filter */}
      <div className="sent-emails-toolbar">
        <div className="sent-emails-search-box">
          <span>🔍</span>

          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search company, contact, recipient or subject..."
          />
        </div>

        <div
          className="sent-emails-period-filter"
          style={periodFilterInlineStyles.wrap}
        >
          <button
            type="button"
            style={{
              ...periodFilterInlineStyles.weeklyButton,
              ...(filterWeekly
                ? periodFilterInlineStyles.weeklyButtonActive
                : null),
            }}
            onClick={() => setFilterWeekly((prev) => !prev)}
          >
            This Week
          </button>

          <select
            className="sent-emails-period-select"
            style={periodFilterInlineStyles.select}
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
          >
            <option value="all">All Years</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <select
            className="sent-emails-period-select"
            style={periodFilterInlineStyles.select}
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
          >
            <option value="all">All Months</option>
            {MONTH_NAMES.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </select>

          <select
            className="sent-emails-period-select"
            style={periodFilterInlineStyles.select}
            value={filterQuarter}
            onChange={(e) => setFilterQuarter(e.target.value)}
          >
            <option value="all">All Quarters</option>
            {QUARTERS.map((q) => (
              <option key={q.key} value={q.key}>
                {getQuarterLabel(
                  q,
                  filterYear === "all" ? new Date().getFullYear() : filterYear,
                )}
              </option>
            ))}
          </select>

          <select
            className="sent-emails-period-select"
            style={{
              ...periodFilterInlineStyles.select,
              minWidth: 140,
            }}
            value={filterDeliveryStatus}
            onChange={(event) => setFilterDeliveryStatus(event.target.value)}
          >
            <option value="all">All Mail Status</option>
            <option value="sent">Sent Only</option>
            <option value="scheduled">Scheduled Only</option>
          </select>

          <select
            className="sent-emails-period-select"
            style={{
              ...periodFilterInlineStyles.select,
              minWidth: 135,
            }}
            value={filterCampaign}
            onChange={(event) => setFilterCampaign(event.target.value)}
          >
            <option value="all">All Campaigns</option>
            {availableCampaigns.map((campaign) => (
              <option key={campaign} value={campaign}>
                {campaign}
              </option>
            ))}
          </select>

          <button
            type="button"
            style={periodFilterInlineStyles.weeklyButton}
            onClick={() => {
              setSearch("");
              setFilterWeekly(false);
              setFilterYear("all");
              setFilterMonth("all");
              setFilterQuarter("all");
              setFilterCampaign("all");
              setFilterDeliveryStatus("all");
            }}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="sent-emails-error">
          <span>{error}</span>

          <button type="button" onClick={fetchSentEmails}>
            Try Again
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="sent-emails-empty">
          <div className="sent-emails-loader" />

          <h3>Loading sent emails...</h3>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredEmails.length === 0 && (
        <div className="sent-emails-empty">
          <div className="sent-emails-empty-icon">
            {currentTypeDetails.icon}
          </div>

          <h3>No {currentTypeDetails.title} Found</h3>

          <p>
            Sent and scheduled emails will appear here. Scheduled emails remain
            marked as Scheduled until the actual delivery time.
          </p>
        </div>
      )}

      {/* Email table */}
      {!loading && !error && filteredEmails.length > 0 && (
        <div className="sent-emails-table-card">
          <div className="sent-emails-table-scroll">
            <table className="sent-emails-table">
              <thead>
                <tr>
                  <th>Delivery Date &amp; Time</th>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Campaign</th>
                  <th>Recipient</th>
                  <th>Subject</th>

                  {activeEmailType === "proposal" && (
                    <>{isAdmin && <th>Proposal Value</th>}</>
                  )}

                  <th>Attachments</th>
                  <th>Sent By</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredEmails.map((email) => (
                  <tr
                    key={email.id}
                    onDoubleClick={() => setSelectedEmail(email)}
                  >
                    <td>
                      <div className="sent-email-date">
                        <strong>
                          {formatDate(
                            email.isScheduled
                              ? email.scheduledFor
                              : email.sentAt,
                          )}
                        </strong>

                        <span>
                          {formatTime(
                            email.isScheduled
                              ? email.scheduledFor
                              : email.sentAt,
                          )}
                        </span>
                      </div>
                    </td>

                    <td>
                      <strong className="sent-email-company">
                        {email.company}
                      </strong>
                    </td>

                    <td>{email.contact}</td>

                    <td>
                      <span className="sent-email-campaign">
                        {email.campaign || "—"}
                      </span>
                    </td>

                    <td>
                      <div className="sent-email-recipients">
                        {email.recipients.length > 0
                          ? email.recipients.map((recipient) => (
                              <span key={recipient}>{recipient}</span>
                            ))
                          : email.leadEmail || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="sent-email-subject">{email.subject}</div>
                    </td>

                    {activeEmailType === "proposal" && (
                      <>
                        {isAdmin && (
                          <td>{formatProposalValue(email.proposalValue)}</td>
                        )}
                      </>
                    )}

                    <td>
                      <span className="sent-email-attachment-count">
                        📎 {email.attachments.length}
                      </span>
                    </td>

                    <td>{email.sentBy}</td>

                    <td>
                      <span
                        className="sent-email-status"
                        style={
                          email.isScheduled
                            ? {
                                background: "#fff7ed",
                                color: "#c2410c",
                                border: "1px solid #fed7aa",
                              }
                            : undefined
                        }
                      >
                        {email.isScheduled
                          ? "◷ Scheduled"
                          : `✓ ${email.status}`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email detail modal */}
      {selectedEmail && (
        <div
          className="sent-email-modal-backdrop"
          onMouseDown={() => setSelectedEmail(null)}
        >
          <div
            className="sent-email-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="sent-email-modal-header">
              <div>
                <span className="sent-email-modal-label">
                  {EMAIL_TYPE_DETAILS[selectedEmail.emailType]?.icon}{" "}
                  {EMAIL_TYPE_DETAILS[selectedEmail.emailType]?.singularTitle}
                </span>

                <h2>{selectedEmail.subject}</h2>
              </div>

              <button
                type="button"
                className="sent-email-modal-close"
                onClick={() => setSelectedEmail(null)}
              >
                ×
              </button>
            </div>

            <div className="sent-email-modal-body">
              <div className="sent-email-detail-grid">
                <div>
                  <span>Company</span>
                  <strong>{selectedEmail.company}</strong>
                </div>

                <div>
                  <span>Contact</span>
                  <strong>{selectedEmail.contact}</strong>
                </div>

                <div>
                  <span>Campaign</span>
                  <strong>{selectedEmail.campaign || "—"}</strong>
                </div>

                <div>
                  <span>Sent By</span>
                  <strong>{selectedEmail.sentBy}</strong>
                </div>

                <div>
                  <span>
                    {selectedEmail.isScheduled
                      ? "Scheduled For"
                      : "Date & Time"}
                  </span>
                  <strong>
                    {formatDate(
                      selectedEmail.isScheduled
                        ? selectedEmail.scheduledFor
                        : selectedEmail.sentAt,
                    )}{" "}
                    {formatTime(
                      selectedEmail.isScheduled
                        ? selectedEmail.scheduledFor
                        : selectedEmail.sentAt,
                    )}
                  </strong>
                </div>

                <div>
                  <span>Status</span>
                  <strong>
                    {selectedEmail.isScheduled
                      ? "Scheduled"
                      : selectedEmail.status}
                  </strong>
                </div>
              </div>

              <div className="sent-email-detail-row">
                <span>To</span>

                <div>
                  {selectedEmail.recipients.length > 0
                    ? selectedEmail.recipients.join(", ")
                    : selectedEmail.leadEmail || "—"}
                </div>
              </div>

              <div className="sent-email-detail-row">
                <span>CC</span>

                <div>
                  {selectedEmail.cc.length > 0
                    ? selectedEmail.cc.join(", ")
                    : "—"}
                </div>
              </div>

              <div className="sent-email-detail-row">
                <span>BCC</span>

                <div>
                  {selectedEmail.bcc.length > 0
                    ? selectedEmail.bcc.join(", ")
                    : "—"}
                </div>
              </div>

              <div className="sent-email-message">
                <span>Message</span>

                <div>
                  {selectedEmail.message || "No message content available."}
                </div>
              </div>

              <div className="sent-email-attachments">
                <span>Attachments</span>

                {selectedEmail.attachments.length === 0 ? (
                  <p>No attachments</p>
                ) : (
                  <div className="sent-email-attachment-list">
                    {selectedEmail.attachments.map((attachment) =>
                      attachment.url ? (
                        <a
                          key={attachment._id}
                          href={attachment.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          📎 {attachment.name}
                        </a>
                      ) : (
                        <span key={attachment._id}>📎 {attachment.name}</span>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="sent-email-modal-footer">
              <button type="button" onClick={() => setSelectedEmail(null)}>
                Close
              </button>

              {selectedEmail.leadId && (
                <button
                  type="button"
                  className="primary"
                  onClick={() => handleOpenLead(selectedEmail.leadId)}
                >
                  {selectedEmail.isScheduled
                    ? "Open Lead / Edit Schedule"
                    : "Open Lead"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SentEmails;
